import React, { useState, useEffect } from 'react';
import { useDataCache } from '../contexts/DataCacheContext';
import configService from '../services/configService';
import LoadingSpinner from './sms/LoadingSpinner';
import ErrorDisplay from './sms/ErrorDisplay';
import DateFilter from './sms/DateFilter';
import SMSTable from './sms/SMSTable';
import SMSModal from './sms/SMSModal';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://oauth.remote-assistant.ai';

const SMSConversations = ({ cachedSmsConversations }) => {
    const { cache } = useDataCache();
    const [allSmsConversations, setAllSmsConversations] = useState([]);
    const [smsConversations, setSmsConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [error, setError] = useState(null);
    const [conversationDetails, setConversationDetails] = useState(null);
    const [hasLoaded, setHasLoaded] = useState(false);
    
    // Date range filter state - default to last 3 days
    const [startDate, setStartDate] = useState(() => {
        const date = new Date();
        date.setDate(date.getDate() - 3);
        return date.toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState(() => {
        return new Date().toISOString().split('T')[0];
    });

    useEffect(() => {
        const data = cachedSmsConversations || cache.logs?.smsConversations;
        if (data) {
            setAllSmsConversations(data);
            setLoading(false);
            setHasLoaded(true);
        } else if (!hasLoaded) {
            loadSMSConversations();
        }
    }, [cachedSmsConversations, cache.logs?.smsConversations]);

    useEffect(() => {
        const startTimestamp = new Date(startDate + 'T00:00:00').getTime() / 1000;
        const endTimestamp = new Date(endDate + 'T23:59:59').getTime() / 1000;
        
        const filtered = allSmsConversations.filter(conv => {
            const convTime = conv.start_time;
            return convTime >= startTimestamp && convTime <= endTimestamp;
        });
        
        setSmsConversations(filtered);
    }, [startDate, endDate, allSmsConversations]);

    const loadSMSConversations = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/oauth/conversations/sms`, {
                headers: { 'Authorization': `Bearer ${await configService.getAuthToken()}` }
            });
            
            if (response.ok) {
                const data = await response.json();
                setAllSmsConversations(data.conversations || []);
                setHasLoaded(true);
            } else {
                throw new Error(`Failed to load SMS conversations (${response.status})`);
            }
        } catch (error) {
            console.error('Failed to load SMS conversations:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const loadConversationMessages = async (conversationId) => {
        setLoadingDetails(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/oauth/conversations/sms/${conversationId}`, {
                headers: { 'Authorization': `Bearer ${await configService.getAuthToken()}` }
            });
            
            if (response.ok) {
                const data = await response.json();
                setMessages(data.messages || []);
                setSelectedConversation(conversationId);
                
                // Find conversation details for modal
                const conversation = smsConversations.find(conv => conv.conversation_id === conversationId);
                if (conversation) {
                    setConversationDetails({
                        from_number: conversation.from_number,
                        to_number: conversation.to_number || smsConversations[0]?.to_number,
                        turn_count: data.messages?.length || 0
                    });
                }
            } else {
                throw new Error(`Failed to load conversation messages (${response.status})`);
            }
        } catch (error) {
            console.error('Failed to load conversation messages:', error);
            setError(error.message);
        } finally {
            setLoadingDetails(false);
        }
    };

    const formatPhoneNumber = (phone) => {
        if (!phone) return 'Unknown';
        const cleaned = phone.replace(/^\+1/, '').replace(/\D/g, '');
        if (cleaned.length === 10) {
            return `${cleaned.slice(0,3)}-${cleaned.slice(3,6)}-${cleaned.slice(6)}`;
        }
        return phone;
    };

    const closeModal = () => {
        setSelectedConversation(null);
        setMessages([]);
        setConversationDetails(null);
    };

    if (loading) {
        return <LoadingSpinner message="Loading SMS conversations..." />;
    }

    if (error) {
        return <ErrorDisplay error={error} onRetry={loadSMSConversations} />;
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex justify-end">
                <button 
                    onClick={loadSMSConversations}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm font-bold flex items-center justify-center"
                >
                    Refresh
                </button>
            </div>
            
            <DateFilter
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={(e) => setStartDate(e.target.value)}
                onEndDateChange={(e) => setEndDate(e.target.value)}
                count={smsConversations.length}
            />

            <SMSTable
                conversations={smsConversations}
                onRowClick={loadConversationMessages}
                formatPhoneNumber={formatPhoneNumber}
            />

            <SMSModal
                isOpen={!!selectedConversation}
                onClose={closeModal}
                loadingDetails={loadingDetails}
                conversationDetails={conversationDetails}
                messages={messages}
                formatPhoneNumber={formatPhoneNumber}
            />
        </div>
    );
};

export default SMSConversations;